import { useEffect, useRef, useState } from 'react'

/**
 * Houdt een stand vast zolang een klik loopt die begon terwijl hij gold (R-139, WCAG 2.5.2).
 *
 * De werkbalk van een rich-text-veld klapt in zodra het veld de focus verliest, en dat gebeurt al bij
 * het indrukken van de muisknop elders. Klapt hij in tussen indrukken en loslaten, dan schuift de pagina:
 * de balk wordt lager, en de browser houdt met scroll anchoring de tekst eronder op zijn plek, dus alles
 * erboven zakt mee. De knop waarop iemand drukte staat bij het loslaten niet meer onder de muis, en de
 * klik gaat naar wat er nu ligt. Gemeten op 1 november 2026 in Chromium op Linux, met de knop Opmerkingen
 * in de kop van het formulier: indrukken op de knop, loslaten op `.ef-contextkop`, 45 pixels lager, en
 * het paneel ging niet open. Op een Mac viel het niet op omdat de pagina daar niet gescrold stond; voor
 * wie de pagina wel gescrold heeft, mist de klik daar net zo.
 *
 * Het inklappen wacht daarom tot de klik voorbij is: tot na het loslaten, en één taak later, zodat de
 * klik zelf eerst afgaat. Wie met het toetsenbord de focus verplaatst drukt geen muisknop in; daar
 * klapt de balk meteen in, zoals voorheen.
 */
export function useHoldDuringPress(active: boolean): boolean {
  const [held, setHeld] = useState(false)
  const current = useRef(active)
  current.current = active

  useEffect(() => {
    let timer: number | undefined
    const down = (): void => {
      if (timer !== undefined) window.clearTimeout(timer)
      timer = undefined
      if (current.current) setHeld(true)
    }
    const up = (): void => {
      if (timer !== undefined) window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        timer = undefined
        setHeld(false)
      }, 0)
    }
    document.addEventListener('pointerdown', down, true)
    document.addEventListener('pointerup', up, true)
    document.addEventListener('pointercancel', up, true)
    return () => {
      if (timer !== undefined) window.clearTimeout(timer)
      document.removeEventListener('pointerdown', down, true)
      document.removeEventListener('pointerup', up, true)
      document.removeEventListener('pointercancel', up, true)
    }
  }, [])

  return active || held
}
